<template>
    <div id="quick-responses">
        <AppBreadCrumb :paths="[{ title: $t('message.defaultContents') }]" :select="true">
            <a-button class="actionButtonBreadcrumb" style="margin-right: 8px" @click="openNewTitleFormModal">
                <md-icon>add</md-icon>
                {{ $t("message.newTitleContent") }}
            </a-button>
            <a-button class="actionButtonBreadcrumb" @click="openNewContentFormModal">
                <md-icon>add</md-icon>
                {{ $t("message.newDefaultContent") }}
            </a-button>
        </AppBreadCrumb>

        <b-container fluid>
            <b-row class="mb-5">
                <b-col v-for="(content, index) in grouped" :key="index" class="mt-5" md="4">
                    <div class="custom-card">
                        <div class="header">
                            <div class="hoverTools">
                                <a-button
                                    icon="plus"
                                    size="small"
                                    type="primary"
                                    @click="openAddContentToTitleFormModal(content, CONTENT_TYPES.ALT)"
                                >
                                    {{ $t(isMac ? "message.newCTRLContent" : "message.newALTContent") }}
                                </a-button>
                                <a-button
                                    icon="plus"
                                    size="small"
                                    type="primary"
                                    @click="openAddContentToTitleFormModal(content, CONTENT_TYPES.TAB)"
                                >
                                    {{ $t("message.newTABContent") }}
                                </a-button>
                            </div>
                            <span>{{
                                content.languageContent[$i18n.locale]
                                    ? content.languageContent[$i18n.locale].name
                                    : content.languageContent["en"].name
                            }}</span>
                            <div class="actions">
                                <a-button class="actionButton d-flex align-items-center" @click="edit(content._id)">
                                    <span style="margin-right: 10px; display: block">{{ $t("message.edit") }}</span>
                                    <ActionEdit />
                                </a-button>
                                <a-button
                                    v-if="!content.hasOwnProperty('children') || content.children.length === 0"
                                    class="actionButton d-flex align-items-center"
                                    @click="remove(content._id)"
                                >
                                    <span style="margin-right: 10px; display: block">{{
                                        $t("message.deleteField")
                                    }}</span>
                                    <ActionDelete />
                                </a-button>
                            </div>
                        </div>
                        <div style="overflow-y: auto">
                            <draggable
                                :id="content._id"
                                :key="content._id"
                                :move="move"
                                :value="getChildren(content)"
                                class="body"
                                group="contents"
                                @end="moveEnd"
                            >
                                <div
                                    v-for="child in getChildren(content)"
                                    :id="child._id"
                                    :key="child._id"
                                    class="item"
                                >
                                    <div
                                        v-tippy="{
                                            followCursor: true,
                                            placement: 'right',
                                            theme: isDarkMode ? 'light-border' : 'google',
                                        }"
                                        :content="
                                            child.languageContent[$i18n.locale]
                                                ? child.languageContent[$i18n.locale].content
                                                : child.languageContent['en'].content
                                        "
                                        class="d-flex align-items-center"
                                        style="cursor: help; width: calc(100% - 100px)"
                                    >
                                        <div class="shortcuts">
                                            <div v-if="child.type === 'ALT'" class="shortcut">
                                                {{ isMac ? "CTRL" : child.type }}
                                                +
                                                {{ child.letter }}
                                            </div>
                                            <div v-else-if="child.type === 'TAB'" class="shortcut">
                                                {{ child.letter }}
                                                <right-button />
                                                {{ child.type }}
                                                <right-button />
                                                {{ child.number }}
                                            </div>
                                        </div>
                                        <div class="contentInfo">
                                            {{
                                                child.languageContent[$i18n.locale]
                                                    ? child.languageContent[$i18n.locale].name
                                                    : child.languageContent["en"].name
                                            }}
                                        </div>
                                    </div>
                                    <div class="actions">
                                        <a-button
                                            class="actionButton d-flex align-items-center"
                                            @click="edit(child._id, true)"
                                        >
                                            <ActionEdit />
                                        </a-button>
                                        <a-button
                                            class="actionButton d-flex align-items-center"
                                            @click="remove(child._id)"
                                        >
                                            <ActionDelete />
                                        </a-button>
                                    </div>
                                </div>
                            </draggable>
                        </div>
                    </div>
                </b-col>
            </b-row>
        </b-container>

        <div class="fab d-flex justify-content-center align-items-center" @click="openNewContentFormModal">
            <md-icon class="">add</md-icon>
        </div>
        <b-modal id="content-form" centered hide-footer hide-header size="lg" @hidden="reset">
            <h3>
                {{ editMode ? $t("message.updateDefaultContentForm") : $t("message.newDefaultContentForm") }}
            </h3>
            <a-alert
                v-if="$validator.errors.items.length && submitClicked"
                :message="$t('message.error')"
                class="mb-2"
                showIcon
                type="error"
            >
                <div slot="description">
                    <div v-for="(err, index) in $validator.errors.items" :key="'err-' + index">
                        <span>
                            {{ err.msg }}
                        </span>
                    </div>
                </div>
            </a-alert>
            <b-form class="d-flex flex-column justify-content-center" @submit.prevent="handleSave">
                <b-row>
                    <b-col md="12">
                        <b-row>
                            <b-col v-if="isChild" md="12">
                                <b-form-group :label="$t('message.contentType')" label-for="type-input">
                                    <b-form-select
                                        id="type-input"
                                        v-model="content.type"
                                        v-validate="'required'"
                                        :data-vv-as="$t('message.contentType')"
                                        :name="$t('message.contentType')"
                                        type="text"
                                    >
                                        <option :value="undefined">{{ $t("message.pickOne") }}...</option>
                                        <option value="ALT">
                                            {{ isMac ? "CTRL" : "ALT" }}
                                        </option>
                                        <option value="TAB">TAB</option>
                                        <option value="TITLE">
                                            {{ $t("message.title") }}
                                        </option>
                                    </b-form-select>
                                </b-form-group>
                            </b-col>
                            <b-col v-if="(content.type || '').toLowerCase() !== 'title'" md="12">
                                <b-form-group id="type-input-group" label="Grup" label-for="type-input">
                                    <b-form-select
                                        id="type-input"
                                        v-model="content.parentId"
                                        :options="
                                            contents.data
                                                .filter((c) => c.parentId === null)
                                                .map((c) => ({
                                                    value: c._id,
                                                    text: c.languageContent[$i18n.locale]
                                                        ? c.languageContent[$i18n.locale].name
                                                        : c.languageContent['en'].name,
                                                }))
                                        "
                                        name="type"
                                        type="text"
                                    >
                                    </b-form-select>
                                </b-form-group>
                            </b-col>
                            <b-col md="12">
                                <b-form-group
                                    v-if="(content.type || '').toLowerCase() === 'alt'"
                                    id="letter-input-group"
                                    :label="$t('message.shortcutChar')"
                                    label-for="letter-input"
                                >
                                    <b-form-input
                                        id="letter-input"
                                        v-model="content.letter"
                                        v-validate="'required|alpha_num|length:1|excluded:e,d,f,z,a,x,c,v,w,t,n'"
                                        :data-vv-as="$t(content.letter)"
                                        :name="$t('message.shortcutChar')"
                                        :placeholder="$t('message.writeSomething')"
                                        autocomplete="off"
                                        trim
                                        type="text"
                                    />
                                    <!--<span class="text-danger mt-3">{{ errors.first('letter') }}</span>-->
                                    <em class="font-weight-light"
                                        >{{ $t("message.shortcut") }}: alt + {{ content.letter }}</em
                                    >
                                    <em class="d-block font-weight-light">{{ $t("message.forMacUsers") }}</em>
                                    <em class="d-block font-weight-light text-danger">{{ $t("message.notValid") }}</em>
                                </b-form-group>
                            </b-col>
                            <b-col md="12">
                                <div>
                                    <b-row
                                        v-if="(content.type || '').toLowerCase() === 'tab'"
                                        class="align-items-center"
                                    >
                                        <b-col md="5">
                                            <b-form-group
                                                :label="$t('message.letterSequence')"
                                                label-for="letter-input"
                                            >
                                                <b-form-input
                                                    id="letter-input"
                                                    v-model="content.letter"
                                                    v-validate="'required'"
                                                    :data-vv-as="$t('message.letterSequence')"
                                                    :name="$t('message.letterSequence')"
                                                    :placeholder="$t('message.exampleText')"
                                                    autocomplete="off"
                                                    trim
                                                    type="text"
                                                />
                                                <!--<span class="text-danger mt-3">{{ errors.first('letter') }}</span>-->
                                            </b-form-group>
                                        </b-col>
                                        <b-col class="text-center" md="2">
                                            <b-badge class="mt-2">TAB</b-badge>
                                        </b-col>
                                        <b-col md="5">
                                            <b-form-group
                                                id="number-input-group"
                                                :label="$t('message.shorcutKey')"
                                                label-for="number-input"
                                            >
                                                <b-form-input
                                                    id="number-input"
                                                    v-model="content.number"
                                                    v-validate="'required|length:1'"
                                                    :data-vv-as="$t('message.shorcutKey')"
                                                    :name="$t('message.shorcutKey')"
                                                    :placeholder="$t('message.anyChar')"
                                                    autocomplete="off"
                                                    trim
                                                />
                                                <!--<span class="text-danger mt-3">{{ errors.first('number') }}</span>-->
                                            </b-form-group>
                                        </b-col>
                                    </b-row>
                                    <b-tabs v-model="selectedLang" card>
                                        <b-tab
                                            v-for="(language, index) in languages"
                                            :key="'tab-' + index"
                                            :title="language.name"
                                        >
                                            <b-form-group id="name-input-group">
                                                <b-form-input
                                                    id="name-input"
                                                    v-model="content.languageContent[language.code].name"
                                                    :data-vv-as="$t('message.contentTitle') + '[' + language.code + ']'"
                                                    :name="$t('message.contentTitle') + '[' + language.code + ']'"
                                                    :placeholder="$t('message.title')"
                                                    :v-validate="language.code === defaultLang ? 'required' : ''"
                                                    autocomplete="off"
                                                    trim
                                                    type="text"
                                                />
                                            </b-form-group>

                                            <b-form-group
                                                v-if="(content.type || '').toLowerCase() !== 'title'"
                                                id="content-input-group"
                                                :label="$t('message.content')"
                                                label-for="content-input"
                                                style="position: relative"
                                            >
                                                <wysiwyg v-model="content.languageContent[language.code].content" />
                                                <ul v-show="showMentionList" class="mentions">
                                                    <li
                                                        v-for="(m, i) in mentions"
                                                        :key="i"
                                                        :class="{ selected: i === mentionIndex }"
                                                    >
                                                        {{ m.text }}
                                                    </li>
                                                </ul>
                                            </b-form-group>
                                        </b-tab>
                                    </b-tabs>
                                </div>
                            </b-col>
                            <b-col md="12">
                                <b-form-group label="Durum">
                                    <b-form-radio-group v-model="content.isActive" name="status" required>
                                        <b-form-radio :value="true">{{ $t("message.active") }}</b-form-radio>
                                        <b-form-radio :value="false">{{ $t("message.passive") }}</b-form-radio>
                                    </b-form-radio-group>
                                </b-form-group>
                            </b-col>
                            <b-col md="12">
                                <b-button :disabled="loading" type="submit" variant="primary">
                                    <template v-if="!loading">
                                        <span v-if="!editMode">
                                            {{ $t("message.save") }}
                                        </span>
                                        <span v-else>
                                            {{ $t("message.update") }}
                                        </span>
                                    </template>
                                    <template v-else>
                                        <font-awesome-icon icon="spinner" spin />
                                    </template>
                                </b-button>
                                <b-button @click="() => this.$bvModal.hide('content-form')">
                                    <template>
                                        {{ $t("message.cancel") }}
                                    </template>
                                </b-button>
                            </b-col>
                        </b-row>
                    </b-col>
                </b-row>
            </b-form>
        </b-modal>

        <ChatBar v-if="!isAdmin" :visible="false" />
    </div>
</template>

<script>
import draggable from "vuedraggable";
import { mapActions, mapGetters, mapState } from "vuex";
import { library } from "@fortawesome/fontawesome-svg-core";
import { faPencilAlt, faPlusCircle, faSpinner, faTimes } from "@fortawesome/free-solid-svg-icons";
import AppBreadCrumb from "../../components/common/AppBreadCrumb";
import { log2 } from "../../utils/log2";
import * as _ from "lodash";
import { CONTENT_TYPES } from "../../utils/staticDatas";
import ChatBar from "../../components/ChatBar";
import { sendSocketMessage } from "../../utils/socketStream";
import ActionDelete from "../../components/svg/ActionDelete";
import ActionEdit from "../../components/svg/ActionEdit";
import RightButton from "../../components/svg/RightButton";
import { isMac } from "../../utils/utils";

library.add(faPencilAlt, faTimes, faPlusCircle, faSpinner);
const log = log2("Contents");
const contentInitialObject = {
    _id: null,
    parentId: null,
    type: undefined,
    letter: "",
    number: "",
    isActive: true,
    lastTargetId: undefined,
    languageContent: {
        tr: {
            name: "",
            description: "",
        },
        en: {
            name: "",
            description: "",
        },
        ru: {
            name: "",
            description: "",
        },
    },
};

export default {
    name: "Contents",
    components: {
        draggable,
        AppBreadCrumb,
        ActionDelete,
        ActionEdit,
        ChatBar,
        RightButton,
    },
    data() {
        return {
            isBusy: true,
            isChild: false,
            loading: false,
            editMode: false,
            submitClicked: false,
            CONTENT_TYPES,
            content: _.cloneDeep(contentInitialObject),
            perPage: 100,
            page: 1,
            isMac,
            mentions: [
                { text: this.$t("message.name"), value: "name" },
                { text: this.$t("message.email"), value: "email" },
            ],
            showMentionList: false,
            mentionIndex: 0,
            selectedLang: null,
        };
    },
    computed: {
        ...mapState({
            contents: (state) => state.content.contents,
            languages: (state) => state.language.languages,
            loggedUser: (state) => state.auth.user,
            isAdmin: (state) => state.auth.adminUser != null,
            site: (state) => state.site.site,
            defaultLang: (state) => state.setting.settings.language.clientDefault,
        }),
        ...mapGetters({
            grouped: "content/getGrouped",
            isDarkMode: "user/isDarkMode",
        }),
    },
    methods: {
        ...mapActions({
            getContents: "content/getContents",
            getContent: "content/getContent",
            createContent: "content/createContent",
            updateContent: "content/updateContent",
            updateParent: "content/updateParent",
            deleteContent: "content/deleteContent",
        }),
        openAddContentToTitleFormModal(parentContent, contentType) {
            this.editMode = false;
            this.isChild = false;
            this.content = {
                ..._.cloneDeep(contentInitialObject),
                type: contentType,
                parentId: parentContent._id,
            };
            this.$bvModal.show("content-form");
            setTimeout(() => {
                this.bindListener();
            }, 300);
        },
        openNewTitleFormModal() {
            this.editMode = false;
            this.isChild = false;
            this.content = {
                ..._.cloneDeep(contentInitialObject),
                type: CONTENT_TYPES.TITLE,
            };
            this.$bvModal.show("content-form");
            setTimeout(() => {
                this.bindListener();
            }, 300);
        },
        openNewContentFormModal() {
            this.editMode = false;
            this.isChild = true;
            this.$bvModal.show("content-form");
            setTimeout(() => {
                this.bindListener();
            }, 300);
        },
        get() {
            this.isBusy = true;
            this.getContents({ perPage: this.perPage, page: this.page }).then(() => (this.isBusy = false));
        },
        edit(id, isChild = false) {
            this.getContent(id).then((response) => {
                this.editMode = true;
                this.isChild = isChild;
                this.content = _.cloneDeep(response);
                this.$bvModal.show("content-form");
                setTimeout(() => {
                    this.bindListener();
                }, 300);
                //hata  mesajı temizlenmesi için eklendi. senkron yapınca kalabiliyor ilginç bir şekilde
                setTimeout(() => {
                    this.$validator.validateAll();
                }, 100);
            });
        },
        remove(id) {
            this.$dialog
                .confirm(this.$t("message.deleteContentQuestion"), {
                    okText: this.$t("message.continue"),
                    cancelText: this.$t("message.cancel"),
                })
                .then(() => {
                    this.deleteContent({ id }).then((res) => {
                        sendSocketMessage("user content-deleted", {
                            contentId: id,
                            siteId: this.site._id,
                        });
                    });
                    this.$toasted.show(this.$t("message.defaultContentDeleted"), {
                        duration: 3000,
                        position: "bottom-center",
                    });
                });
        },
        reset() {
            this.content = _.cloneDeep(contentInitialObject);
            this.submitClicked = false;
        },
        handleSave() {
            this.submitClicked = true;
            this.$validator.validateAll().then((response) => {
                this.loading = true;
                Object.keys(this.content.languageContent).forEach((key) => {
                    if (!this.content.languageContent[key].name) {
                        this.content.languageContent[key].content = this.content.languageContent[
                            this.defaultLang
                        ].content;
                        this.content.languageContent[key].name = this.content.languageContent[this.defaultLang].name;
                    }
                });
                if (response) {
                    const result = this.content._id
                        ? this.updateContent(this.content).then((res) => {
                              sendSocketMessage("user content-updated", {
                                  content: this.content,
                                  siteId: this.site._id,
                              });
                          })
                        : this.createContent(this.content).then((res) => {
                              sendSocketMessage("user new-content-added", {
                                  content: this.content,
                                  siteId: this.site._id,
                              });
                          });

                    result
                        .then(() => {
                            this.$bvModal.hide("content-form");
                            this.$toasted.show(this.$t("message.defaultContentSaved"), {
                                duration: 3000,
                                position: "bottom-center",
                            });
                            this.get();
                            this.reset();
                        })
                        .catch((err) => {
                            log.error(err.response, err.message);
                            const errMsg =
                                err.response && err.response.data && err.response.data.localizedMessages
                                    ? err.response.data.localizedMessages[this.$i18n.locale]
                                    : err.message;
                            this.$toasted.error(errMsg, {
                                duration: 4000,
                                position: "bottom-center",
                            });
                        })
                        .finally(() => {
                            this.loading = false;
                            this.submitClicked = false;
                        });
                } else {
                    this.loading = false;
                }
            });
        },
        moveEnd(e) {
            log("moveEnd", e, e.item.id, this.lastTargetId);
            if (e.item.id !== this.lastTargetId) {
                const movingItem = _.find(this.contents.data, (q) => q._id === e.item.id);
                log("movedItem", movingItem, e.item.id, this.contents.data);
                if (movingItem) {
                    const isParent = movingItem.parentId === null;
                    const containsAnySubItem = this.getChildren(movingItem).length > 1;
                    log("move end", {
                        movingItem: movingItem.languageContent.tr.name,
                        isParent,
                        childrenCount: this.getChildren(movingItem).length,
                    });
                    if (isParent && containsAnySubItem) {
                        log.error("this parent item with children, cannot move");

                        this.$toasted.error(this.$t("message.cannotMoveFullParent"), {
                            duration: 2000,
                            position: "bottom-center",
                        });
                    } else {
                        this.updateParent({
                            _id: e.item.id,
                            parentId: this.lastTargetId,
                        }).then(() => {
                            this.$toasted.show(this.$t("message.contentMoved"), {
                                duration: 3000,
                                position: "bottom-center",
                            });
                        });
                    }
                }
            }
        },
        move(e) {
            log("move called", e, e.dragged, e.to);
            if (e.dragged.id !== e.to.id) {
                const movingItem = _.find(this.contents.data, (q) => q._id === e.dragged.id);
                log("movingItem", movingItem, e.dragged.id, this.contents.data);
                this.lastTargetId = e.to.id;
                if (movingItem) {
                    const isParent = movingItem.parentId === null;
                    const containsAnySubItem = this.getChildren(movingItem).length > 1;
                    log("move before", {
                        movingItem: movingItem.languageContent.tr.name,
                        isParent,
                        childrenCount: this.getChildren(movingItem).length,
                    });
                    if (isParent && containsAnySubItem) {
                        return false;
                    } else {
                        return true;
                    }
                }
            } else {
                return false;
            }
        },
        getChildren(content) {
            const item = _.cloneDeep(content);
            let items = item.children || [];
            items.push({
                ...item,
                children: undefined,
            });
            items = _.uniqBy(items, (q) => q._id).filter((x) => x.type !== CONTENT_TYPES.TITLE);
            log.debug("getChildren", { item, items });
            return items;
        },
        bindListener() {
            let editors = document.querySelectorAll(".editr div[contenteditable=true]");

            let eventHandler = (e) => (event) => {
                let range = window.getSelection().getRangeAt(0);
                switch (event.key) {
                    case "@":
                        let mentions = document.querySelector(".mentions");
                        let modalRect = e.getBoundingClientRect();
                        let curRect = range.getBoundingClientRect();
                        mentions.style.left = `${curRect.left - modalRect.left}px`;
                        mentions.style.top = `${curRect.top - modalRect.top}px`;
                        this.showMentionList = true;
                        break;
                    case "ArrowDown":
                        if (!this.showMentionList) break;
                        this.mentionIndex =
                            this.mentionIndex === this.mentions.length - 1 ? this.mentionIndex : this.mentionIndex + 1;
                        event.preventDefault();
                        break;
                    case "ArrowUp":
                        if (!this.showMentionList) break;
                        this.mentionIndex = this.mentionIndex === 0 ? 0 : this.mentionIndex - 1;
                        event.preventDefault();
                        break;
                    case "Enter":
                        if (!this.showMentionList) break;
                        this.showMentionList = false;
                        let textNode = document.createTextNode(this.mentions[this.mentionIndex].value);
                        range.insertNode(textNode);
                        event.preventDefault();
                        break;
                    default:
                        this.showMentionList = false;
                        break;
                }
            };

            for (let e of editors) {
                e.addEventListener("keydown", eventHandler(e));
            }
        },
    },
    mounted() {
        this.$validator.localize(this.$i18n.locale);
        console.log("local dil", this.$i18n.locale);
        this.get();
        log("mounted", this.grouped);
    },
    updated() {
        if (this.$validator.locale !== this.$i18n.locale) this.$validator.localize(this.$i18n.locale);
    },
};
</script>

<style lang="scss" scoped>
.mentions {
    z-index: 999999;
    background-color: #8787c7;
    color: #ffffff;
    padding: 0;
    margin: 0;
    list-style: none;
    border-radius: 0.6rem;
    display: inline-block;
    overflow: hidden;
    position: absolute;
    top: 70px;
    left: 10px;
    box-shadow: 0 0 3px #00000059;

    li {
        padding: 0.3rem 1.3rem;
        cursor: pointer;
        border-bottom: 1px solid #5a5a85;

        &:last-child {
            border-bottom: none;
        }

        &:hover,
        &.selected {
            background-color: #4f4fab;
        }
    }
}

.new-theme {
    #quick-responses {
        .custom-card {
            background-color: #ffffff;
            border-radius: 6px;
            padding: 10px;
            padding-top: 0;
            position: relative;

            min-height: 350px;
            height: 350px;
            max-height: 350px;

            .header {
                &:hover .hoverTools {
                    opacity: 1;
                    transform: translateY(0);
                }

                padding-top: 10px;
            }

            .hoverTools {
                position: absolute;
                bottom: 100%;
                left: 0;
                right: 0;
                display: flex;
                justify-content: space-around;
                transition: all 0.1s;
                opacity: 0;
                transform: translateY(100%);
            }

            .header {
                display: flex;
                justify-content: space-between;
                align-items: center;
                border-bottom: 1px solid #3699ff;
                padding-bottom: 10px;

                span {
                    display: block;
                    font-family: Poppins;
                    font-size: 16px;
                    font-weight: 300;
                    color: #181c32;
                }

                .actions {
                    display: flex;

                    .actionButton {
                        border: none;
                        box-shadow: none;
                        padding: 0px 10px;
                    }
                }
            }

            .body {
                min-height: 250px;
                height: 250px;
                max-height: 250px;

                .item {
                    height: 30px;
                    margin: 8px 0;
                    border-radius: 6px;

                    cursor: move;

                    display: flex;
                    justify-content: space-between;

                    background-color: #ffffff;

                    .shortcuts {
                        margin-right: 6px;
                    }

                    .shortcut {
                        background-color: #eaecf2;
                        font-family: Poppins;
                        color: #3699ff;
                        font-size: 14px;
                        font-weight: 300;
                        text-align: center;
                        border-radius: 3px;
                        padding: 5px 5px 5px 5px;
                        white-space: nowrap;
                    }

                    .contentInfo {
                        color: #181c32;
                        font-size: 14px;
                        font-weight: 300;
                        padding-top: 5px;
                        display: block;
                        overflow: hidden;
                        white-space: nowrap;
                        text-overflow: ellipsis;
                        padding-left: 5px;
                    }

                    .actions {
                        display: flex;

                        .actionButton {
                            border: none;
                            box-shadow: none;
                            height: 30px;
                        }
                    }
                }
            }
        }
    }
}

.fab {
    width: 50px;
    height: 50px;
    border-radius: 25px;
    background-color: white;
    position: absolute;
    bottom: 20px;
    left: 100px;
    z-index: 9999;
    transition: all 0.3s;
    box-shadow: 0 0 0 0 rgba(100, 100, 100, 1);
    transform: scale(1);
    animation: pulse 3s infinite;
    cursor: pointer;
}

@keyframes pulse {
    0% {
        transform: scale(0.95);
        box-shadow: 0 0 0 0 rgba(100, 100, 100, 0.5);
    }

    70% {
        transform: scale(1);
        box-shadow: 0 0 0 10px #00000000;
    }

    100% {
        transform: scale(0.95);
        box-shadow: 0 0 0 0 #00000000;
    }
}
</style>
